// Bytes: the NetData binary format (the content bundle),
// base64, raw deflate, SHA-256 and the content fingerprint. Works in browsers and Node.js (both have TextEncoder,
// CompressionStream and WebCrypto).

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });

export const utf8 = (text: string): Uint8Array => encoder.encode(text);

/** Thrown for data that's cut short or damaged. */
export class InvalidDataError extends Error {
  override name = 'InvalidDataError';
}

/**
 * Builds NetData bytes: little-endian numbers, strings as a ushort (UTF-8 byte count + 1; 0 =
 * empty) then the bytes.
 */
export class NetWriter {
  private data = new Uint8Array(64);
  private view = new DataView(this.data.buffer);
  length = 0;

  private take(count: number): number {
    if (this.length + count > this.data.length) {
      const bigger = new Uint8Array(Math.max(this.data.length * 2, this.length + count));
      bigger.set(this.data);
      this.data = bigger;
      this.view = new DataView(bigger.buffer);
    }
    const at = this.length;
    this.length += count;
    return at;
  }

  // take() may replace the buffer, so each takes its offset before touching it.
  byte(v: number): this { const at = this.take(1); this.data[at] = v & 0xff; return this; }
  ushort(v: number): this { const at = this.take(2); this.view.setUint16(at, v, true); return this; }
  int(v: number): this { const at = this.take(4); this.view.setInt32(at, v, true); return this; }
  bytes(b: Uint8Array): this { const at = this.take(b.length); this.data.set(b, at); return this; }

  /** A string cut to maxLength UTF-16 code units (0 = no limit). */
  string(value: string, maxLength = 0): this {
    if (!value) return this.ushort(0);
    const b = utf8(maxLength > 0 && value.length > maxLength ? value.slice(0, maxLength) : value);
    if (b.length >= 0xffff) throw new RangeError('string over 65534 bytes');
    return this.ushort(b.length + 1).bytes(b);
  }

  toBytes(): Uint8Array { return this.data.slice(0, this.length); }
}

/** Reads NetData bytes; reading past the end or invalid UTF-8 throws InvalidDataError. */
export class NetReader {
  private readonly view: DataView;
  position: number;

  constructor(private readonly data: Uint8Array, offset = 0, private readonly end = data.length) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    this.position = offset;
  }

  get available(): number { return this.end - this.position; }

  private take(count: number): number {
    if (count > this.available) throw new InvalidDataError(`Not enough data to read ${count} byte(s) at ${this.position} of ${this.end}.`);
    const at = this.position;
    this.position += count;
    return at;
  }

  byte(): number { return this.data[this.take(1)]; }
  ushort(): number { return this.view.getUint16(this.take(2), true); }
  int(): number { return this.view.getInt32(this.take(4), true); }
  long(): bigint { return this.view.getBigInt64(this.take(8), true); }
  bytes(count: number): Uint8Array { const at = this.take(count); return this.data.slice(at, at + count); }
  bytesWithLength(): Uint8Array { return this.bytes(this.ushort()); }

  /** A string; one longer than maxLength (0 = no limit) reads as empty. */
  string(maxLength = 0): string {
    const size = this.ushort();
    if (size === 0) return '';
    let value: string;
    try { value = decoder.decode(this.bytes(size - 1)); }
    catch { throw new InvalidDataError('Invalid UTF-8 in a string.'); }
    return maxLength > 0 && value.length > maxLength ? '' : value;
  }
}

export function concat(...parts: Uint8Array[]): Uint8Array {
  const all = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) { all.set(p, at); at += p.length; }
  return all;
}

export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function fromBase64(text: string): Uint8Array {
  const s = atob(text);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes;
}

async function pipe(data: Uint8Array, stream: CompressionStream | DecompressionStream, max: number): Promise<Uint8Array> {
  const writer = stream.writable.getWriter();
  writer.write(data as Uint8Array<ArrayBuffer>).catch(() => {});
  writer.close().catch(() => {});
  const chunks: Uint8Array[] = [];
  let total = 0;
  const reader = stream.readable.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.length;
      if (total > max) throw new InvalidDataError('data too big once decompressed');
      chunks.push(value);
    }
  } catch (e) {
    if (e instanceof InvalidDataError) throw e;
    throw new InvalidDataError(`damaged compressed data (${(e as Error).message})`);
  } finally {
    reader.releaseLock();
  }
  return concat(...chunks);
}

/** Raw deflate , up to max bytes decompressed. */
export const inflate = (data: Uint8Array, max = 64 * 1024 * 1024) => pipe(data, new DecompressionStream('deflate-raw'), max);
export const deflate = (data: Uint8Array) => pipe(data, new CompressionStream('deflate-raw'), Number.MAX_SAFE_INTEGER);

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', data as Uint8Array<ArrayBuffer>));
}

export { contentHash } from '../../../shared/src/hash';

/** Little-endian 64-bit unsigned from 8 bytes (the terrain hash's first 8 bytes, read little-endian). */
export function readUint64LE(bytes: Uint8Array, offset = 0): bigint {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getBigUint64(offset, true);
}
